/**
 * Apply coordinator review decisions to timeline events, mirroring the
 * backend's censuszen/timeline/review.py so both sides agree on what
 * "the reviewed timeline" means.
 */

/**
 * @param {Object} event - Timeline event as stored on the case doc.
 * @param {Object} review - timeline_review map keyed by event id.
 * @returns {string} The event's status after any reviewer correction.
 */
export function effectiveStatus(event, review) {
    const entry = (review || {})[event?.id] || {};
    return entry.corrected_status || event?.status || 'unknown';
}

/**
 * @param {Array<Object>} events - Timeline events from the case doc.
 * @param {Object} review - timeline_review map keyed by event id.
 * @returns {Array<Object>} Copies of the non-dismissed events with
 * corrected statuses applied. Unknown review ids are ignored.
 */
export function applyTimelineReview(events, review) {
    const rev = review || {};
    return (events || [])
        .filter((e) => e && rev[e.id]?.decision !== 'dismissed')
        .map((e) => {
            const corrected = rev[e.id]?.corrected_status;
            return corrected ? { ...e, status: corrected } : { ...e };
        });
}

/**
 * The review gate: every event whose effective status is "active" must have
 * a review entry (confirmed or dismissed). Dismissing satisfies the gate;
 * correcting a status counts as review by construction.
 *
 * @param {Array<Object>} events - Timeline events from the case doc.
 * @param {Object} review - timeline_review map keyed by event id.
 * @returns {boolean}
 */
export function allActiveEventsReviewed(events, review) {
    const rev = review || {};
    return (events || []).every((e) => {
        if (!e) return true;
        if (effectiveStatus(e, rev) !== 'active') return true;
        return !!rev[e.id]?.decision;
    });
}

/**
 * @param {Array<Object>} events - Timeline events from the case doc.
 * @param {Object} review - timeline_review map keyed by event id.
 * @returns {{reviewed: number, total: number}} Progress over the events
 * that currently gate (effective status "active").
 */
export function activeReviewProgress(events, review) {
    const rev = review || {};
    const active = (events || []).filter(
        (e) => e && effectiveStatus(e, rev) === 'active'
    );
    const reviewed = active.filter((e) => !!rev[e.id]?.decision);
    return { reviewed: reviewed.length, total: active.length };
}
